import React, { useEffect, useState } from 'react';
import cx from 'classnames';

import Button from '~/components/button';
import Modal from '~/components/modals/modal';
import ProductCarousels from '~/components/products/product-detail/product-carousels';
import { Field, Form } from '~/components/form';
import SelectField from '~/components/inputs/select-field';
import { FEATURE_ICONS } from '~/components/plans/bundles-page/plan-bundle';
import { useCartMutations } from '~/features/cart/cart-actions';
import { useFlashMessage } from '~/features/flash-messages/flash-message-actions';
import { captureException } from '~/utils/exception-tracking';
import { centsToCurrency } from '~/utils/numbers';
import { required, valid } from '~/utils/validation';
import { useCart } from '~/hooks/use-cart';
import { toSentenceCase } from '~/utils/strings';
import { useProductData } from '~/features/products/products-actions';

import styles from '~/components/modals/pest-bundle/pest-bundle-modal.module.scss';
import modalStyles from '~/components/modals/modal.module.scss';
import carouselStyles from '~/components/products/product-detail/product-carousel.module.scss';

const PestBundleModal = ({ bundles, channel, isOpen, onClose, largePlan }) => {
  return (
    <Modal
      className={cx(modalStyles.productDetail, styles.pestBundleModal)}
      onClose={onClose}
      isOpen={isOpen}
    >
      <PestBundleModalContent
        bundles={bundles}
        channel={channel}
        isOpen={isOpen}
        onClose={onClose}
        largePlan={largePlan}
      />
    </Modal>
  );
};

export default PestBundleModal;

function PestBundleModalContent({
  bundles,
  channel,
  isOpen,
  onClose,
  largePlan,
}) {
  const [selectedBundle, setSelectedBundle] = useState();
  const { products } = useProductData();

  useEffect(() => {
    if (isOpen && bundles && !selectedBundle) {
      setSelectedBundle(bundles[0]);
    }
  }, [selectedBundle, bundles, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedBundle(null);
    }
  }, [isOpen]);

  if (!bundles) {
    return null;
  }

  const handleClose = () => {
    setSelectedBundle(null);
    onClose();
  };

  const handleQtyFieldChange = ({ target }) =>
    setSelectedBundle(bundles.find((plan) => plan.size === target.value));

  const images = [];
  if (largePlan?.primaryImage) {
    images.push(largePlan.primaryImage);
  }
  if (largePlan?.images) {
    images.push(...largePlan.images);
  }

  return (
    <>
      <div className={styles.container}>
        <div className={carouselStyles.productModal}>
          <ProductCarousels images={images} />
        </div>

        <div className={styles.contentWrapper}>
          <h2 className={styles.title}>{selectedBundle?.title}</h2>
          <h3 className={cx(styles.price, styles.priceDesktop)}>
            {centsToCurrency(selectedBundle?.unitPrice, { round: true })}
            <span> / year</span>
          </h3>

          <div className={styles.features}>
            <h3>Here's what you get</h3>
            <ul className={styles.contentsList}>
              {selectedBundle?.features?.map((feature) => (
                <li key={feature.text} className={styles.contentsItem}>
                  <div className={styles.contentsItemIcon}>
                    <img
                      src={
                        FEATURE_ICONS[feature.icon]?.src ||
                        FEATURE_ICONS.default.src
                      }
                      style={{
                        height:
                          FEATURE_ICONS[feature.icon]?.height ||
                          FEATURE_ICONS.default.height,
                        width: 'auto',
                      }}
                      alt={feature.icon}
                    />
                  </div>
                  <p className={styles.contentsItemQuantity}>
                    {feature.quantity}
                  </p>
                  <p className={styles.contentsItemText}>{feature.text}</p>
                </li>
              ))}
            </ul>
          </div>

          <div className={styles.addToCart}>
            <AddBundleForm
              channel={channel}
              onClose={handleClose}
              onFieldChange={handleQtyFieldChange}
              selectedBundle={selectedBundle}
              products={products}
            />
          </div>
        </div>
      </div>

      <div className={styles.mobileContainer}>
        <h2 className={styles.title}>{selectedBundle?.title}</h2>

        <div className={carouselStyles.productModal}>
          <ProductCarousels images={images} />
        </div>

        <div className={styles.addToCart}>
          <AddBundleForm
            channel={channel}
            onClose={handleClose}
            onFieldChange={handleQtyFieldChange}
            selectedBundle={selectedBundle}
            products={products}
          />
        </div>

        <div className={styles.features}>
          <h3>Here's what you get</h3>
          <ul className={styles.contentsList}>
            {selectedBundle?.features?.map((feature) => (
              <li key={feature.text} className={styles.contentsItem}>
                <div className={styles.contentsItemIcon}>
                  <img
                    src={
                      FEATURE_ICONS[feature.icon]?.src ||
                      FEATURE_ICONS.default.src
                    }
                    style={{
                      height:
                        FEATURE_ICONS[feature.icon]?.height ||
                        FEATURE_ICONS.default.height,
                      width: 'auto',
                    }}
                    alt={feature.icon}
                  />
                </div>
                <p className={styles.contentsItemQuantity}>
                  {feature.quantity}
                </p>
                <p className={styles.contentsItemText}>{feature.text}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
}

const AddBundleForm = ({ channel, onClose, onFieldChange, selectedBundle }) => {
  const { showFlashMessage } = useFlashMessage();
  const { cart } = useCart();
  const { addToCart } = useCartMutations();

  const planExists = Boolean(
    cart?.contents?.find(
      (item) => item.sku.skuId === selectedBundle?.purchaseSku.skuId
    )
  );

  const handleSubmit = async () => {
    if (planExists) {
      return showFlashMessage(
        `${selectedBundle.title} - ${toSentenceCase(
          selectedBundle.size
        )} is in your cart, please select another size.`,
        'error'
      );
    }

    try {
      await addToCart(selectedBundle.purchaseSku, 1, { isSubscription: true });

      showFlashMessage(
        `${selectedBundle.title} - ${toSentenceCase(
          selectedBundle.size
        )} added to your cart`,
        'success',
        5000
      );

      onClose();
    } catch (err) {
      captureException(new Error('Error when adding a pest plan to cart'), {
        extras: {
          error: JSON.stringify(err),
          planSku: JSON.stringify(selectedBundle.purchaseSku),
        },
      });

      showFlashMessage(
        'Oops, there was a problem adding the plan to your cart',
        'error'
      );
    }

    return;
  };

  const validate = (formValues) => {
    const errors = {};
    if (!formValues.size) {
      errors.size = valid(required('Please select plan'))(formValues.size);
    }

    return errors;
  };

  return (
    <Form
      onSubmit={handleSubmit}
      validate={validate}
      initialValues={{ size: 'small' }}
    >
      {({ isSubmitting }) => (
        <div className={styles.addProductFormSelectButtonWrapper}>
          <div className={styles.addBundleFormSelectWrapper}>
            <h3 className={cx(styles.price, styles.priceMobile)}>
              {centsToCurrency(selectedBundle?.unitPrice, { round: true })}
              <span> / year</span>
            </h3>
            <Field
              label="size"
              id="size"
              name="size"
              variant="standalone"
              component={SelectField}
              placeholder="Select plan"
              onChange={onFieldChange}
            >
              <option value="small">Small</option>
              <option value="medium">Medium</option>
              <option value="large">Large</option>
            </Field>
          </div>

          <Button
            className={styles.addBundleFormButton}
            type="submit"
            variant="primary"
            isLoading={isSubmitting}
            fullWidth
          >
            Add to cart
          </Button>
        </div>
      )}
    </Form>
  );
};
